import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Clock, Users, Star, DollarSign, CheckCircle } from 'lucide-react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import CityTourBookingForm from '../../components/CityTourBookingForm';

const JinjaKayaking = () => {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);

  const tourDetails = {
    title: 'Kayaking',
    price: 50,
    duration: '3 hours'
  };

  return (
    <>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white">
        <div className="absolute inset-0">
          <img 
            src="https://imgur.com/Wa3J7KL.jpg"
            alt="Kayaking"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/80"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Link to="/uganda-city-tours" className="inline-flex items-center text-white/90 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to City Tours
            </Link>
          </div>
          
          <div className="max-w-3xl">
            <div className="inline-flex items-center bg-blue-500/20 rounded-full px-2.5 py-0.5 text-xs text-blue-500 mb-4 backdrop-blur-sm">
              Water Sport
            </div>
            <h1 className="text-4xl font-bold mb-4">Kayaking Adventure on the Nile</h1>
            
            <div className="flex flex-wrap gap-4 mb-6">
              <div className="flex items-center">
                <MapPin className="h-5 w-5 text-ta-green mr-1" />
                <span>Jinja</span>
              </div>
              <div className="flex items-center">
                <Clock className="h-5 w-5 text-ta-green mr-1" />
                <span>3 hours</span>
              </div>
              <div className="flex items-center">
                <Users className="h-5 w-5 text-ta-green mr-1" />
                <span>1-8 people</span>
              </div>
              <div className="flex items-center">
                <Star className="h-5 w-5 text-yellow-400 fill-current mr-1" />
                <span>4.8 (86 reviews)</span>
              </div>
            </div>
            
            <button
              onClick={() => setIsBookingModalOpen(true)}
              className="px-6 py-3 bg-ta-green text-white rounded-lg hover:bg-ta-green-dark transition-colors"
            >
              Book Now - $50 per person
            </button>
          </div>
        </div>
      </section>

      {/* Tour Details */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Experience Overview</h2>
              <p className="text-gray-600 mb-6">
                Paddle along the calm waters of the Nile River in this guided kayaking adventure. Perfect for 
                both beginners and experienced kayakers, this tour offers a unique perspective of the river's 
                beauty and wildlife.
              </p>

              <h3 className="text-xl font-bold text-gray-900 mb-4">Experience Highlights</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-ta-green mr-2 mt-0.5" />
                  <span>Professional instruction</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-ta-green mr-2 mt-0.5" />
                  <span>Quality equipment</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-ta-green mr-2 mt-0.5" />
                  <span>Safety briefing</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-ta-green mr-2 mt-0.5" />
                  <span>Scenic routes</span>
                </div>
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-4">What to Expect</h3>
              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Introduction & Safety</h4>
                  <p className="text-gray-600">
                    Begin with a comprehensive introduction to kayaking techniques and safety procedures. 
                    Get fitted with appropriate equipment and learn basic paddling skills.
                  </p>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">On the Water</h4>
                  <p className="text-gray-600">
                    Paddle through calm waters while your guide shares insights about the river ecosystem. 
                    Spot various bird species and enjoy the peaceful surroundings.
                  </p>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Skills Development</h4>
                  <p className="text-gray-600">
                    Practice different paddling techniques and maneuvers in a safe environment. 
                    Progress at your own pace with support from experienced instructors.
                  </p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="sticky top-24 bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Experience Information</h3>
                  
                  <div className="space-y-4 mb-6">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Price</span>
                      <span className="font-semibold text-gray-900">$50 per person</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Duration</span>
                      <span className="font-semibold text-gray-900">3 hours</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Group Size</span>
                      <span className="font-semibold text-gray-900">1-8 people</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Difficulty</span>
                      <span className="font-semibold text-gray-900">Beginner-Friendly</span>
                    </div>
                  </div>

                  <button
                    onClick={() => setIsBookingModalOpen(true)}
                    className="w-full py-3 bg-ta-green text-white rounded-lg hover:bg-ta-green-dark transition-colors"
                  >
                    Book Now
                  </button>

                  <div className="mt-6 space-y-3">
                    <div className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-600">Professional instructors</span>
                    </div>
                    <div className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-600">All kayaking equipment</span>
                    </div>
                    <div className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-600">Safety gear</span>
                    </div>
                    <div className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-600">Bottled water</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Form Modal */}
      <CityTourBookingForm
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        tourTitle={tourDetails.title}
        tourPrice={tourDetails.price}
        tourDuration={tourDetails.duration}
      />

      <Footer />
    </>
  );
};

export default JinjaKayaking;